import type { PermissionTuple } from '~/utils/ability'
import type { RouteTo } from '~/utils/rbac'
import type { GuardedTab } from '~/utils/rbacTabs'

/** Chaque onglet d'une boutique est une URL distincte : l'onglet courant est déduit de la route. */
export type StoreTab =
  | 'stocks'
  | 'details'
  | 'inventory'
  | 'inventories'
  | 'hours'
  | 'kiosks'
  | 'stats'

/**
 * Source unique des droits de lecture des sous-onglets. Consommée par le `staticData.permission` de
 * chaque route d'onglet, par la barre d'onglets, et par `/stores/$storeId` qui n'est accessible que
 * si l'un d'eux l'est.
 */
export const STORE_TAB_PERMISSIONS = {
  stocks: ['read', 'stores.store-stocks.stock'],
  details: ['read', 'stores.store-list.store'],
  inventory: ['read', 'stores.current-inventory.inventory'],
  inventories: ['read', 'stores.all-inventories.history'],
  hours: ['read', 'stores.schedules.schedule'],
  kiosks: ['read', 'stores.kiosks.kiosk'],
  stats: ['read', 'stores.statistics.statistics'],
} as const satisfies Record<StoreTab, PermissionTuple>

/**
 * Les routes d'onglet, c'est-à-dire toutes celles de la fiche boutique sauf le détail d'un
 * inventaire — le seul descendant à demander un second param. Ce resserrement rend `storeId`
 * suffisant pour naviguer vers n'importe quel onglet.
 */
type StoreTabRoute = Exclude<
  Extract<RouteTo, `/stores/$storeId/${string}`>,
  '/stores/$storeId/inventories/$inventoryId'
>

/** Chaque onglet a forcément une route et un droit de lecture — d'où les champs rendus requis. */
type StoreTabDefinition = GuardedTab<StoreTab> & {
  to: StoreTabRoute
  permission: PermissionTuple
}

/**
 * Ordre de déclaration = ordre d'affichage, et ordre de repli de la redirection `/stores/$storeId`.
 *
 * `legacyRights` reproduit la visibilité d'avant la migration tant que le flag `permission-v2` est
 * OFF : seuls « Gestion des horaires » et « Gestion des kiosks » étaient ouverts au-delà des
 * lecteurs de stock (typiquement `SUPPORT_OPERATOR`).
 */
export const STORE_TABS: StoreTabDefinition[] = [
  {
    value: 'stocks',
    label: 'Stocks',
    to: '/stores/$storeId/stocks',
    permission: STORE_TAB_PERMISSIONS.stocks,
    legacyRights: ['STOCK_READER'],
  },
  {
    value: 'details',
    label: 'Informations',
    to: '/stores/$storeId/details',
    permission: STORE_TAB_PERMISSIONS.details,
    legacyRights: ['STOCK_READER'],
  },
  {
    value: 'inventory',
    label: 'Inventaire en cours',
    to: '/stores/$storeId/inventory',
    permission: STORE_TAB_PERMISSIONS.inventory,
    legacyRights: ['STOCK_READER'],
  },
  {
    value: 'inventories',
    label: 'Tous les inventaires',
    to: '/stores/$storeId/inventories',
    permission: STORE_TAB_PERMISSIONS.inventories,
    legacyRights: ['STOCK_READER'],
  },
  {
    value: 'hours',
    label: 'Gestion des horaires',
    to: '/stores/$storeId/hours',
    permission: STORE_TAB_PERMISSIONS.hours,
  },
  {
    value: 'kiosks',
    label: 'Gestion des kiosks',
    to: '/stores/$storeId/kiosks',
    permission: STORE_TAB_PERMISSIONS.kiosks,
  },
  {
    value: 'stats',
    label: 'Statistiques',
    to: '/stores/$storeId/stats',
    permission: STORE_TAB_PERMISSIONS.stats,
    legacyRights: ['STOCK_READER'],
  },
]

/** Les sept droits de lecture, pour la garde `{ any: [...] }` de `/stores/$storeId`. */
export const STORE_TAB_READ_PERMISSIONS: PermissionTuple[] = STORE_TABS.map((tab) => tab.permission)
